import React, { useState, useEffect, useRef } from 'react';

const GRID_CLASS_MAP = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  bullets: 'grid-cols-1',
};

const ExamQuestionPage = ({ page, selectedAnswer, onAnswer }) => {
  const [localSelection, setLocalSelection] = useState(null);
  const previousPageId = useRef(page.id);

  useEffect(() => {
    if (previousPageId.current !== page.id) {
      previousPageId.current = page.id;
      setLocalSelection(selectedAnswer || null);
    }
  }, [page.id, selectedAnswer]);

  const handleOptionClick = (optionId) => {
    setLocalSelection(optionId);
    onAnswer(optionId);
  };

  const getOptionStyle = (option) => {
    if (localSelection === option.id) {
      return 'border-blue-500 bg-blue-100 font-semibold cursor-pointer';
    }
    return 'cursor-pointer hover:bg-gray-50 border-gray-300';
  };

  const renderTabularOption = (option) => {
    const config = page.tableConfig || {};
    const styling = config.styling || {};
    const questionNum = Number(page.questionNumber);
    const shouldShowBullets = [1, 9, 20].includes(questionNum);

    const columns = config.columns || 2;

    const gridClass = shouldShowBullets
      ? GRID_CLASS_MAP.bullets
      : GRID_CLASS_MAP[columns] || GRID_CLASS_MAP[2];

    return (
      <button
        key={option.id}
        onClick={() => handleOptionClick(option.id)}
        className={`
          w-full max-w-3xl mx-auto text-left border-2 rounded-lg transition-colors 
          ${getOptionStyle(option)} 
          ${styling.cellBackground || 'bg-white'} 
          ${styling.cellTextColor || 'text-gray-700'}
        `}
      >
        <div className={`grid ${gridClass} ${styling.border ? 'border border-gray-300' : ''} rounded-lg overflow-hidden`}>
          {option.cells.map((cell) => {
            const isHeader = cell.type === 'header';

            const forceBullets = shouldShowBullets && !isHeader;

            return (
              <div
                key={cell.id}
                className={`
                  ${isHeader ? (styling.headerBackground || 'bg-gray-100') : ''} 
                  ${isHeader ? (styling.headerTextColor || 'text-gray-800') : ''} 
                  ${isHeader ? 'px-4 py-3 font-bold text-center' : 'px-6 py-4 text-center'}
                `}
              >
                {forceBullets ? (
                  <ul className="list-disc list-inside space-y-1.5 text-sm mx-auto max-w-prose leading-relaxed">
                    {cell.text
                      .split('\n')
                      .filter((line) => line.trim())
                      .map((line, idx) => (
                        <li key={idx} className="text-left">
                          {line.trim()}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <div className="whitespace-pre-line">
                    {cell.text.split('\n').map((line, idx) => (
                      <div key={idx} className={`${isHeader ? 'font-bold' : ''} text-center`}>
                        {line}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </button>
    );
  };

  const renderStandardOption = (option) => (
    <button
      key={option.id}
      onClick={() => handleOptionClick(option.id)}
      className={`
        w-full max-w-3xl mx-auto text-left p-4 border-2 rounded-lg transition-colors 
        ${getOptionStyle(option)}
      `}
    >
      <span className="font-semibold mr-2">{option.label}.</span>
      {option.text}
    </button>
  );

  return (
    <div className="max-w-4xl mx-auto px-4">
      <div className="mb-4">
        <span className="text-sm text-gray-600">
          Question {page.questionNumber} of {page.totalQuestions}
        </span>
      </div>

      <h2 className="text-xl font-semibold mb-6 text-gray-800">
        {page.question.text}
      </h2>

      <div className="space-y-4 mb-6">
        {page.options.map((option) =>
          page.optionsType === 'tabular'
            ? renderTabularOption(option)
            : renderStandardOption(option)
        )}
      </div>
    </div>
  );
};

export default ExamQuestionPage;